import { Map as OlMap } from 'ol';
import { Projection } from 'ol/proj.js';
import { View } from 'ol';
import { NgZone } from '@angular/core';
import { defaults as defaultInteractions, DragPan, Interaction, MouseWheelZoom } from 'ol/interaction.js';
import {
  LayerManagerModel, LayerSwipeModel, MapExportOptions, MapExportResult, MapViewDetailsModel, MapViewerModel, MapViewerOptionsModel, OlMapStyleType,
} from '../models';
import { ProjectionsHelper } from '../helpers/projections.helper';
import { OpenlayersExtent } from '../models/extent.type';
import { OpenLayersLayerManager } from './open-layers-layer-manager';
import {
  BehaviorSubject, combineLatest, concatMap, filter, forkJoin, map, merge, Observable, of, race, switchMap, take, timer,
} from 'rxjs';
import { Size } from 'ol/size.js';
import { ToolManagerModel } from '../models/tool-manager.model';
import { OpenLayersToolManager } from './open-layers-tool-manager';
import { OpenLayersEventManager } from './open-layers-event-manager';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom.js';
import { buffer, Extent, extend, getCenter } from 'ol/extent.js';
import { Layer as BaseLayer } from 'ol/layer.js';
import { OpenLayersWmsGetFeatureInfoHelper } from './helpers/open-layers-wms-get-feature-info.helper';
import { HttpClient, HttpXsrfTokenExtractor } from '@angular/common/http';
import { ErrorResponseModel, FeatureModel } from '@tailormap-viewer/api';
import { OpenLayersMapImageExporter } from './openlayers-map-image-exporter';
import { Attribution } from 'ol/control.js';
import { mouseOnly, platformModifierKeyOnly } from 'ol/events/condition.js';
import { CesiumManager } from './cesium-map/cesium-manager';
import { CesiumEventManager } from './cesium-map/cesium-event-manager';
import { OlMapScaleHelper } from '../helpers/ol-map-scale.helper';
import { OpenLayersSnappingManager } from './openlayers-snapping-manager';
import { FeatureModelType } from '../models/feature-model.type';
import { OpenLayersLayerSwipe } from './open-layers-layer-swipe';

export class OpenLayersMap implements MapViewerModel {

  private map: BehaviorSubject<OlMap | null> = new BehaviorSubject<OlMap | null>(null);
  private layerManager: BehaviorSubject<OpenLayersLayerManager | null> = new BehaviorSubject<OpenLayersLayerManager | null>(null);
  private toolManager: BehaviorSubject<ToolManagerModel | null> = new BehaviorSubject<ToolManagerModel | null>(null);

  // Holds the container passed to render(), which may happen before or after initMap() has created a
  // map. Paired with `map` below so the map gets bound to its container whichever of the two arrives last.
  private container: BehaviorSubject<HTMLElement | null> = new BehaviorSubject<HTMLElement | null>(null);
  private observedContainer: HTMLElement | null = null;

  private map3d: BehaviorSubject<CesiumManager | null> = new BehaviorSubject<CesiumManager | null>(null);
  private made3d: boolean;
  private in3d: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly resizeObserver: ResizeObserver;
  private initialExtent: OpenlayersExtent = [];
  private initialCenterZoom?: [ number[], number ] = undefined;
  private mapPadding: number[] | undefined;

  private hasUserInteractedSubject = new BehaviorSubject(false);

  private eventManager = new OpenLayersEventManager();
  private cesiumEventManager = new CesiumEventManager();
  private snappingManager = new OpenLayersSnappingManager();
  private layerSwipe = new OpenLayersLayerSwipe();

  constructor(
    private ngZone: NgZone,
    private httpXsrfTokenExtractor: HttpXsrfTokenExtractor,
  ) {
    this.resizeObserver = new ResizeObserver(() => this.updateMapSize());
    this.made3d = false;

    const isNotNullContainer = (item: HTMLElement | null): item is HTMLElement => item !== null;
    combineLatest([ this.getMap$(), this.container.asObservable().pipe(filter(isNotNullContainer)) ])
      .subscribe(([ olMap, container ]) => this.ngZone.runOutsideAngular(() => this.bindMapToContainer(olMap, container)));
  }

  public initMap(options: MapViewerOptionsModel, initialOptions?: { initialCenter?: [number, number]; initialZoom?: number }) {
    ProjectionsHelper.initProjection(options.projection, options.projectionDefinition, options.projectionAliases);
    if (typeof initialOptions?.initialCenter !== 'undefined' && typeof initialOptions?.initialZoom !== 'undefined') {
      this.initialCenterZoom = [ initialOptions.initialCenter, initialOptions.initialZoom ];
    }
    const view = new View({
      projection: options.projection,
      extent: options.maxExtent,
      center: initialOptions?.initialCenter,
      zoom: initialOptions?.initialZoom,
      showFullExtent: true,
    });
    if (this.map.value && this.map.value.getView().getProjection().getCode() === options.projection) {
      this.map.value.setView(view);
      if (this.initialCenterZoom !== undefined) {
        this.map.value.getView().setCenter(this.initialCenterZoom[0]);
        this.map.value.getView().setZoom(this.initialCenterZoom[1]);
      } else if (options.initialExtent && options.initialExtent.length > 0) {
        this.map.value.getView().fit(options.initialExtent);
      }
      return;
    }
    const isInIframe = window.self !== window.top;
    const olMap = new OlMap({
      controls: [],
      interactions: defaultInteractions({
        altShiftDragRotate: false,
        pinchRotate: false,
        mouseWheelZoom: !isInIframe,
        dragPan: !isInIframe,
      }).extend([
        new Interaction({
          handleEvent: event => {
            if (event.originalEvent instanceof KeyboardEvent && event.originalEvent.key === '=') {
              event.originalEvent.preventDefault();
              const mapView = event.map.getView();
              mapView.animate({ zoom: (mapView.getZoom() ?? 0) + 1, duration: 100 });
              return false;
            }
            return true;
          },
        }),
        ...(isInIframe ? [
          new DragPan({
            condition: function (event) {
              return event.activePointers?.length === 2 || mouseOnly(event);
            },
          }),
          new MouseWheelZoom({ condition: platformModifierKeyOnly }),
        ] : []),
      ]),
      view,
    });
    // always add the attribution control
    const attributionControl = new Attribution({
      className: `ol-attribution ol-attribution--${options.controlOptions?.attributionPosition}`,
      collapsed: false,
      collapseLabel: options.controlOptions?.attributionPosition === 'left' ? '‹' : '›',
    });
    olMap.addControl(attributionControl);

    this.initialExtent = options.initialExtent?.length === 4
      ? options.initialExtent
      : options.maxExtent;

    if (this.toolManager.value) {
      this.toolManager.value.destroy();
    }

    if (this.layerManager.value) {
      this.layerManager.value.destroy();
    }

    if (this.map.value) {
      this.map.value.dispose();
    }

    this.eventManager.destroy();
    this.snappingManager.destroy();

    const layerManager = new OpenLayersLayerManager(olMap, this.ngZone, this.httpXsrfTokenExtractor);
    layerManager.init();
    this.eventManager.initEvents(olMap, this.ngZone, this.in3d);
    this.snappingManager.init(olMap, layerManager);
    this.layerSwipe.init(olMap);
    const toolManager = new OpenLayersToolManager(olMap, this.ngZone, this.eventManager, this.cesiumEventManager, this.snappingManager);

    // Collapse the attribution control after 5 seconds, or the first time the user zooms, pans, or clicks on the map
    merge(
      timer(5000),
      this.eventManager.onMapClick$(),
      this.eventManager.onMapMoveStart$(),
    )
      .pipe(
        take(1),
      )
      .subscribe(() => {
        attributionControl?.setCollapsed(true);
      });

    race([ this.eventManager.onMapClick$(), this.eventManager.onMapMoveStart$() ])
      .pipe(take(1))
      .subscribe(() => this.hasUserInteractedSubject.next(true));

    this.map.next(olMap);
    this.layerManager.next(layerManager);
    this.toolManager.next(toolManager);
  }

  public render(container: HTMLElement) {
    this.container.next(container);
  }

  public getLayerManager$(): Observable<LayerManagerModel> {
    const isLayerManager = (item: LayerManagerModel | null): item is LayerManagerModel => item !== null;
    return this.layerManager.asObservable().pipe(filter(isLayerManager));
  }

  public getToolManager$(): Observable<ToolManagerModel> {
    const isToolManager = (item: ToolManagerModel | null): item is ToolManagerModel => item !== null;
    return this.toolManager.asObservable().pipe(filter(isToolManager));
  }

  public getVisibleExtent$(): Observable<OpenlayersExtent> {
    return this.getSize$().pipe(
      concatMap(size => this.getMap$().pipe(
        map(olMap => olMap.getView().calculateExtent(size)),
      )),
    );
  }

  public setZoomLevel(zoom: number) {
    this.executeMapAction(olMap => olMap.getView().setZoom(zoom));
  }

  public zoomIn() {
    this.executeMapAction(olMap => {
      olMap.getView().setZoom((olMap.getView().getZoom() || 0) + 1);
    });
  }

  public zoomOut() {
    this.executeMapAction(olMap => {
      olMap.getView().setZoom((olMap.getView().getZoom() || 0) - 1);
    });
  }

  public zoomToInitialExtent() {
    this.executeMapAction(olMap => {
      if (this.initialExtent && this.initialExtent.length > 0) {
        olMap.getView().fit(this.initialExtent);
      }
    });
  }

  public zoomToScale(scale: number) {
    this.executeMapAction(olMap => {
      const view = olMap.getView();
      const resolution = OlMapScaleHelper.getResolutionForScale(view.getProjection(), scale);
      if (resolution) {
        const zoom = view.getZoomForResolution(resolution);
        if (zoom) {
          view.setZoom(zoom);
        }
      }
    });
  }

  /**
   * Removes the map's mouse-wheel-zoom interaction (whichever variant it was configured with, see
   * `initMap`), so scrolling the wheel over the map no longer zooms it. There's no matching enable
   * method: this is meant for pages that embed the map inline in a scrolling document and want the
   * page's own scroll to win over the map's default zoom-by-wheel behaviour.
   */
  public disableMouseWheelZoom() {
    this.executeMapAction(olMap => {
      olMap.getInteractions().getArray()
        .filter((interaction): interaction is MouseWheelZoom => interaction instanceof MouseWheelZoom)
        .forEach(interaction => olMap.removeInteraction(interaction));
    });
  }

  public setLayerSwipe(swipe: LayerSwipeModel | null) {
    this.layerSwipe.setSwipe(swipe);
  }

  private getFeaturesExtent(olFeatures: Feature<Geometry>[]) {
    if (olFeatures.length === 0) {
      return;
    }
    const extents = olFeatures
      .map(f => f.getGeometry()?.getExtent())
      .filter((e): e is Extent => typeof e !== "undefined");
    if (extents.length === 0) {
      return;
    }
    const totalExtent = extents[0];
    extents.slice(1).forEach(e => extend(totalExtent, e));
    return totalExtent;
  }

  public setCenterAndZoom(center: number[], zoom: number) {
      this.initialCenterZoom = [ center, zoom ];

      this.executeMapAction(olMap => {
          const view = olMap.getView();
          view.setCenter(center);
          view.setZoom(zoom);
      });
  }

  public centerFeatures(olFeatures: Feature<Geometry>[]) {
    const totalExtent = this.getFeaturesExtent(olFeatures);
    if (!totalExtent) {
      return;
    }
    this.executeMapAction(olMap => {
      olMap.getView().setCenter(getCenter(totalExtent));
    });
  }

  public zoomToFeatures(olFeatures: Feature<Geometry>[], flyTo = false) {
    const totalExtent = this.getFeaturesExtent(olFeatures);
    if (!totalExtent) {
      return;
    }
    this.zoomToExtent(totalExtent, undefined, flyTo);
  }

  public zoomToGeometry(geom?: Geometry, maxZoom?: number) {
    if (!geom) {
      return;
    }
    this.zoomToExtent(geom.getExtent(), maxZoom);
  }

  public zoomToExtent(extent: Extent, maxZoom?: number, flyTo = false) {
    this.executeMapAction(olMap => {
      const bufferedExtent = buffer(extent, 10);
      if (flyTo) {
        this.flyToExtent(olMap, bufferedExtent, maxZoom);
        return;
      }
      olMap.getView().fit(bufferedExtent, { duration: 1000, padding: this.mapPadding, maxZoom: maxZoom });
    });
  }

  /**
   * Animates the view to the given extent with a 'fly-to' effect: instead of a straight linear
   * pan/zoom, the view zooms out while panning to the target center and then zooms back in to
   * the target's resolution (the classic OpenLayers flyTo recipe).
   */
  private flyToExtent(olMap: OlMap, extent: Extent, maxZoom?: number, duration = 2000) {
    const view = olMap.getView();
    const size = olMap.getSize();
    const currentZoom = view.getZoom();
    const resolution = size ? view.getResolutionForExtent(extent, size) : undefined;
    const targetZoom = resolution ? view.getZoomForResolution(resolution) : undefined;
    if (typeof currentZoom !== 'number' || typeof targetZoom !== 'number') {
      view.fit(extent, { duration, padding: this.mapPadding, maxZoom });
      return;
    }
    const clampedTargetZoom = typeof maxZoom === 'number' ? Math.min(targetZoom, maxZoom) : targetZoom;
    const dipZoom = Math.min(currentZoom, clampedTargetZoom) - 1;
    // A single chained call (rather than two parallel `animate()` calls) keeps the zoom-out and the
    // pan+zoom-in as one animation series, so they can't drift out of sync or race with a
    // previously started flight; cancel any animation still running first so a new fly-to always
    // starts from a clean state instead of competing with a stale one.
    view.cancelAnimations();
    view.animate(
      { zoom: dipZoom, duration: duration / 2 },
      { center: getCenter(extent), zoom: clampedTargetZoom, duration: duration / 2 },
    );
  }

  public zoomTo(center: number[], zoomLevel?: number, animationDuration = 1000, ignoreWhileAnimating = false) {
    this.executeMapAction(olMap => {
      if (olMap.getView().getAnimating() && ignoreWhileAnimating) {
        return;
      }
      zoomLevel = !(zoomLevel) || zoomLevel < 0 ? olMap.getView().getZoom() : zoomLevel;
      if (typeof zoomLevel === 'undefined') {
        return;
      }
      if (animationDuration === 0) {
        olMap.getView().setCenter(center);
        olMap.getView().setZoom(zoomLevel);
      } else {
        olMap.getView().animate({ duration: animationDuration, zoom: zoomLevel, center });
      }
    });
  }

  public setPadding(padding: number[]) {
    this.mapPadding = padding;
  }

  public getMap$(): Observable<OlMap> {
    const isNotNullMap = (item: OlMap | null): item is OlMap => item !== null;
    return this.map.asObservable().pipe(filter(isNotNullMap));
  }

  public executeMapAction(fn: (olMap: OlMap) => void) {
    this.getMap$()
      .pipe(take(1))
      .subscribe(olMap => fn(olMap));
  }

  public getProjection$(): Observable<Projection> {
    return this.getMap$().pipe(map(olMap => olMap.getView().getProjection()));
  }

  public getPixelForCoordinates$(coordinates: [number, number]): Observable<[number, number] | null> {
    return merge(
      this.getMap$(),
      this.eventManager.onMapMove$().pipe(map(evt => evt.map)))
        .pipe(
          map(olMap => {
            const px = olMap.getPixelFromCoordinate(coordinates);
            if (!px) {
              return null;
            }
            return [ px[0], px[1] ];
          }),
        );
  }

  public getMapViewDetails$(): Observable<MapViewDetailsModel> {
    return merge(
      this.getMap$(),
      this.eventManager.onMapMove$().pipe(map(evt => evt.map)))
      .pipe(
        map(olMap => {
          const view = olMap.getView();
          const { scale, resolution } = OlMapScaleHelper.getResolutionAndScale(view);
          return {
            zoomLevel: view.getZoom() || 0,
            minZoomLevel: view.getMinZoom() || 0,
            maxZoomLevel: view.getMaxZoom() || 0,
            resolution,
            minResolution: view.getMinResolution() || 0,
            maxResolution: view.getMaxResolution() || 0,
            scale,
            size: olMap.getSize(),
            center: olMap.getView().getCenter() != null ? olMap.getView().getCenter() : undefined,
            extent: olMap.getView().getCenter() != null ? olMap.getView().calculateExtent(olMap.getSize()) : null,
          };
        }),
      );
  }

  public exportMapImage$(options: MapExportOptions): Observable<MapExportResult> {
    return this.getMap$().pipe(
      take(1),
      concatMap((olMap: OlMap) => {
        const extraLayers: BaseLayer[] = olMap.getAllLayers().filter(l => options.vectorLayerFilter && options.vectorLayerFilter(l));
        return forkJoin([
          of(extraLayers),
          OpenLayersMapImageExporter.exportMapImage$(olMap.getSize() as Size, olMap.getView(), options, extraLayers, this.ngZone, this.httpXsrfTokenExtractor),
        ]);
      }),
      map(([ extraLayers, exportResult ]) => {
        // Force redraw of extra layers with normal DPI
        extraLayers.forEach(l => l.changed());
        return exportResult;
      }),
    );
  }

  public getFeatureInfoForLayers$(
    layerId: string,
    coordinates: [number, number],
    httpClient: HttpClient,
  ): Observable<FeatureModel[] | ErrorResponseModel> {
    return forkJoin([
      this.layerManager.asObservable().pipe(
        filter((layerManager: OpenLayersLayerManager | null): layerManager is OpenLayersLayerManager => layerManager !== null),
        take(1),
      ),
      this.getMap$().pipe(take(1)),
    ])
      .pipe(
        concatMap(([ layerManager, olMap ]) => {
          return OpenLayersWmsGetFeatureInfoHelper.getFeatureInfoForLayer$(
            httpClient,
            layerId,
            coordinates,
            olMap.getView().getResolution() || 0,
            olMap.getView().getProjection().getCode(),
            layerManager,
          );
        }),
      );
  }

  private getSize$(): Observable<Size> {
    return this.getMap$().pipe(map(olMap => {
      const size = olMap.getSize();
      if (!size) {
        return [ 0, 0 ];
      }
      return size;
    }));
  }

  private bindMapToContainer(olMap: OlMap, container: HTMLElement) {
    if (this.observedContainer && this.observedContainer !== container) {
      this.resizeObserver.unobserve(this.observedContainer);
    }
    this.observedContainer = container;

    olMap.setTarget(container);
    olMap.render();
    if (this.initialCenterZoom !== undefined) {
        olMap.getView().setCenter(this.initialCenterZoom[0]);
        olMap.getView().setZoom(this.initialCenterZoom[1]);
    } else if (this.initialExtent && this.initialExtent.length > 0) {
      olMap.getView().fit(this.initialExtent);
    }
    window.setTimeout(() => this.updateMapSize(), 0);
    this.resizeObserver.observe(container);
  }

  private updateMapSize() {
    this.executeMapAction(olMap => {
      olMap.updateSize();
    });
  }

  public getCesiumManager$(): Observable<CesiumManager> {
    const isCesiumManager = (item: CesiumManager | null): item is CesiumManager => item !== null;
    return this.map3d.asObservable().pipe(filter(isCesiumManager));
  }

  public executeCesiumAction(fn: (cesiumManager: CesiumManager) => void) {
    this.getCesiumManager$()
      .pipe(take(1))
      .subscribe(cesiumManager => fn(cesiumManager));
  }

  public make3d(){
    if (!this.made3d) {
      this.made3d = true;
      this.executeMapAction(olMap => {
        this.map3d.next(new CesiumManager(olMap, this.ngZone, this.cesiumEventManager, this.map.getValue()?.getView().getProjection()));
      });
      this.executeCesiumAction(cesiumManager => {
        cesiumManager.init();
      });
    }
  }

  public switch3d(){
    this.executeCesiumAction(cesiumManager => {
      cesiumManager.switch3d();
    });
    this.in3d.next(!this.in3d.value);
  }

  public getIn3d$(): Observable<boolean> {
    return this.in3d.asObservable();
  }

  public set3dTerrainOpacity(opacity: number) {
    this.executeCesiumAction(cesiumManager => {
      cesiumManager.setTerrainOpacity(opacity);
    });
  }

  public get3dTerrainOpacity$(): Observable<number> {
    return this.getCesiumManager$().pipe(
      switchMap(cesiumManager => cesiumManager.getTerrainOpacity$()),
    );
  }

  public hasUserInteractedWithMap$(): Observable<boolean> {
    return this.hasUserInteractedSubject.asObservable();
  }

  public getPointerDrag$(): Observable<void> {
    return this.eventManager.onPointerDrag$().pipe(map(() => undefined));
  }

  public allowSnapping(allow: boolean) {
    this.snappingManager.allowSnapping(allow);
  }

  public setSnappingLayerStyle(style: OlMapStyleType) {
    this.snappingManager.setSnappingLayerStyle(style);
  }

  public setSnappingTolerance(tolerance: number) {
    this.snappingManager.setSnappingTolerance(tolerance);
  }

  public renderSnappingFeatures(features: FeatureModelType[]) {
    return this.snappingManager.renderFeatures(features);
  }

}
